<template>
  <PmDataModalPurchaseOrderPure
    :id="id"
    :is-loading="isLoading"
    :name="purchaseOrder?.caption"
    :number="purchaseOrder?.number"
    :status="status"
    :ordered-at="parseServerDateString(purchaseOrder?.orderedAt)"
    :created-date="parseServerDateString(purchaseOrder?.createdAt)"
    :created-by="purchaseOrder?.createdBy?.displayName"
    :edited-date="parseServerDateString(purchaseOrder?.updatedAt)"
    :edited-by="purchaseOrder?.updatedBy?.displayName"
    :type="purchaseOrder?.purchaseOrderType?.caption"
    :supplier="purchaseOrder?.supplier?.company"
    :supplier-contact="supplierContact"
    :supplier-phone="supplierPhone"
    :supplier-email="purchaseOrder?.contactSupplier?.email"
    :arranged-by="purchaseOrder?.arrangedBy?.displayName"
    :is-cleared="purchaseOrder?.isCleared"
    :received-at="parseServerDateString(purchaseOrder?.receivedAt)"
    :end-at="parseServerDateString(purchaseOrder?.endAt)"
    :payment-condition="purchaseOrder?.paymentCondition?.caption"
    :total-price="totalPrice"
    :description="purchaseOrder?.description"
    :supplier-note="purchaseOrder?.supplierNote"
    :resources="resources"
    @open-resource="openResource"
    @open-resource-allocation="openResourceAllocation"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'

import { DATA_MODAL_TYPE } from '@/constants/persoplan'
import { EVENT } from '@/constants/events'
import { parseServerDateString } from '@/utilities/date'
import { getPurchaseOrderStatus } from '@/composition/usePurchaseOrderStatus'
import EventHub from '@/eventHub'

import PmDataModalPurchaseOrderPure, {
  type Props as PropsDataModalPurchaseOrderPure,
} from '@/components/persoplan/DataModal/DataModalPurchaseOrder/PmDataModalPurchaseOrderPure.vue'

import { DataModalPurchaseOrderDocument } from '../../../../../generated/graphql'

export interface Props {
  id: number
}

const props = defineProps<Props>()

const purchaseOrderQuery = useQuery(
  DataModalPurchaseOrderDocument,
  () => ({ id: props.id }),
  () => ({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })
)

const purchaseOrder = computed(
  () => purchaseOrderQuery.result.value?.purchaseOrder
)

const isLoading = computed(() => purchaseOrderQuery.loading.value === true)

const status = computed(() => {
  const purchaseOrderStatus = getPurchaseOrderStatus({
    id: purchaseOrder.value?.purchaseOrderState?.id,
  })
  if (!purchaseOrderStatus) return

  const label = purchaseOrder.value?.purchaseOrderState?.caption
  if (!label) return

  const result: PropsDataModalPurchaseOrderPure['status'] = {
    status: purchaseOrderStatus,
    label,
  }

  return result
})

const supplierContact = computed(() => {
  const contact = purchaseOrder.value?.contactSupplier
  if (!contact) return

  return [contact.firstName, contact.surname].filter(Boolean).join(' ')
})

const supplierPhone = computed(() => {
  const contact = purchaseOrder.value?.contactSupplier
  if (!contact) return

  return contact.phone ?? contact.mobile
})

const resources = computed(() => {
  const result: PropsDataModalPurchaseOrderPure['resources'] = []

  purchaseOrder.value?.resourceFunctionAllocations?.forEach(
    (resourceFunctionAllocation) => {
      if (!resourceFunctionAllocation) return

      result.push({
        id: resourceFunctionAllocation.id,
        title: resourceFunctionAllocation.resourceFunction?.caption,
        abbreviation: resourceFunctionAllocation.resourceFunction?.abbreviation,
        startDate: parseServerDateString(resourceFunctionAllocation.startDate),
        endDate: parseServerDateString(resourceFunctionAllocation.endDate),
        resourceAllocations:
          resourceFunctionAllocation.resourceAllocations?.flatMap(
            (resourceAllocation) => {
              if (!resourceAllocation) return []

              const title =
                resourceAllocation.address?.displayName ??
                resourceAllocation.vehicle?.caption

              if (!title) return []

              return [{ id: resourceAllocation.id, title }]
            }
          ) ?? [],
      })
    }
  )

  return result
})

function openResource(id: number) {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.RESOURCE,
    id,
  })
}

function openResourceAllocation(id: number) {
  EventHub.$emit(EVENT.DATA_MODAL_SHOW, {
    type: DATA_MODAL_TYPE.RESOURCE_ALLOCATION,
    id,
  })
}

const totalPrice = computed(() => {
  const price = purchaseOrder.value?.totalPrice
  const currency = purchaseOrder.value?.currencyTarget?.threeLetterCode
  if (price === null || price === undefined) return
  if (!currency) return String(price)

  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(price)
})
</script>
